import { z } from 'zod';
import { PIPELINE_NODE_TYPE_NAME } from '../types/pipeline.types.js';
// ── Graph sub-schemas ──
const pipelineNodeSchema = z.object({
    id: z.string().min(1).max(100),
    // Shape only (K4 erratum C2): whether this composition serves the type is the api's validateGraph check.
    type: z.string().max(100).regex(PIPELINE_NODE_TYPE_NAME),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.object({
        label: z.string().min(1).max(200),
        config: z.record(z.unknown()).default({}),
        description: z.string().max(500).optional(),
    }),
});
const pipelineEdgeSchema = z.object({
    id: z.string().min(1).max(100),
    source: z.string().min(1),
    target: z.string().min(1),
    sourceHandle: z.string().optional(),
    targetHandle: z.string().optional(),
    label: z.string().max(200).optional(),
    animated: z.boolean().optional(),
});
const pipelineGraphSchema = z.object({
    nodes: z.array(pipelineNodeSchema).max(200),
    edges: z.array(pipelineEdgeSchema).max(500),
    viewport: z.object({ x: z.number(), y: z.number(), zoom: z.number() }).optional(),
});
const pipelineVariableSchema = z.object({
    name: z.string().min(1).max(100),
    type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
    defaultValue: z.unknown().optional(),
    description: z.string().max(500).optional(),
});
const triggerConfigSchema = z.object({
    type: z.enum(['manual', 'cron', 'event', 'webhook', 'pipeline']),
    cron: z.object({ schedule: z.string().max(100) }).optional(),
    // gh#3174 — the shape pipeline.eventListener reads (event.types); eventType was never read.
    event: z.object({
        types: z.array(z.string().min(1).max(200)).min(1).max(20),
        filter: z.record(z.unknown()).optional(),
    }).optional(),
    webhook: z.object({ secret: z.string().optional() }).optional(),
}).nullable().optional();
// ── Pipeline CRUD ──
export const pipelineCreateSchema = z.object({
    name: z.string().min(1).max(200),
    slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/).optional(),
    description: z.string().max(1000).optional(),
    profileId: z.number().int().positive().nullable().optional(),
    graph: pipelineGraphSchema.optional(),
    variables: z.array(pipelineVariableSchema).max(50).optional(),
    triggerConfig: triggerConfigSchema,
    status: z.enum(['draft', 'active', 'archived']).optional(),
    isTemplate: z.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
});
export const pipelineUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    profileId: z.number().int().positive().nullable().optional(),
    graph: pipelineGraphSchema.optional(),
    variables: z.array(pipelineVariableSchema).max(50).optional(),
    triggerConfig: triggerConfigSchema,
    status: z.enum(['draft', 'active', 'archived']).optional(),
});
// ── Run ──
// F12 Sprint C — simulation sub-schema
export const simulationContextSchema = z.object({
    mode: z.enum(['mock', 'replay', 'stub']),
    mockProviderConfig: z.object({
        modelOverride: z.string().max(80).optional(),
        profileIdOverride: z.number().int().positive().optional(),
    }).optional(),
    replayRunId: z.number().int().positive().optional(),
    fixtureId: z.number().int().positive().optional(),
}).optional();
export const pipelineRunCreateSchema = z.object({
    triggerType: z.enum(['manual', 'cron', 'event', 'webhook', 'pipeline']).optional(),
    triggerData: z.record(z.unknown()).optional(),
    debugMode: z.boolean().optional(),
    breakpoints: z.array(z.string().max(100)).max(200).optional(),
    simulation: simulationContextSchema,
});
// ── Generate ──
export const pipelineGenerateSchema = z.object({
    prompt: z.string().min(1).max(5000),
    profileId: z.number().int().positive().optional(),
});
// ── Query & Params ──
export const pipelineListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(20),
    search: z.string().max(200).optional(),
    status: z.enum(['draft', 'active', 'archived']).optional(),
    isTemplate: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
});
export const pipelineIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
// itemId=2656 — zod strips unknown keys, so `:version` under pipelineIdParamSchema reached the service as NaN.
// Both columns are PG `integer`: past int4 the driver's out-of-range error is a 500, so the cap is a 400 here.
const PG_INT4_MAX = 2_147_483_647;
export const pipelineVersionParamSchema = z.object({
    id: z.coerce.number().int().positive().max(PG_INT4_MAX),
    version: z.coerce.number().int().positive().max(PG_INT4_MAX),
});
export const pipelineRunIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
    runId: z.coerce.number().int().positive(),
});
export const pipelineRunListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(20),
    status: z.enum(['pending', 'running', 'paused', 'completed', 'failed', 'cancelled']).optional(),
});
//# sourceMappingURL=pipeline.schema.js.map